import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getEmployeeSelfReviewCyclesAPI, 
  getEmployeeAppraisalResponsesAPI
} from "api/apiHelper";
import { AvailableCycle, AppraisalResponse } from "types/appraisal-response-types";
import Card from "components/Card";
import Button from "components/Button";
import { toast } from "react-toastify";

const SelfReviewTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selfReviewCycles, setSelfReviewCycles] = useState<AvailableCycle[]>([]);
  const [completedSelfResponses, setCompletedSelfResponses] = useState<AppraisalResponse[]>([]);

  useEffect(() => {
    fetchSelfReviewCycles();
    fetchCompletedResponses();
  }, []);

  const fetchSelfReviewCycles = async () => {
    try {
      setLoading(true);
      const response = await getEmployeeSelfReviewCyclesAPI();
      setSelfReviewCycles(response.data);
    } catch (err: any) {
      toast.error("Failed to load self review cycles");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedResponses = async () => {
    try {
      const response = await getEmployeeAppraisalResponsesAPI('SELF', 'SUBMITTED');
      setCompletedSelfResponses(response.data);
    } catch (err: any) {
      console.error("Failed to load completed responses:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isDeadlinePassed = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  if (loading && selfReviewCycles.length === 0) {
    return (
      <Card extra="p-4">
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          Loading self reviews...
        </div>
      </Card>
    );
  }

  return (
    <Card extra="p-4">
      <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
        Self Reviews
      </h2>

      {selfReviewCycles.length === 0 ? (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          No self review cycles available at this time.
        </div>
      ) : (
        <div className="space-y-4">
          {selfReviewCycles.map((cycle) => {
            const completedResponse = completedSelfResponses.find(r => r.cycle.id === cycle.id);
            const isCompleted = !!completedResponse;

            return (
              <div
                key={cycle.id}
                className="rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700"
                onClick={() => {
                  if (isCompleted) {
                    navigate(`/employee-appraisals/response/${completedResponse.id}`);
                  } else if (!isDeadlinePassed(cycle.deadline)) {
                    navigate(`/employee-appraisals/self/${cycle.id}`);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                      {cycle.name}
                    </h3>
                    {cycle.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {cycle.description}
                      </p>
                    )}
                  </div>
                  {isCompleted ? (
                    <span className="px-2 py-1 text-xs font-semibold text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-navy-600 rounded-full flex-shrink-0">
                      Submitted
                    </span>
                  ) : isDeadlinePassed(cycle.deadline) ? (
                    <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 rounded">
                      Deadline Passed
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    {isCompleted && completedResponse?.submitted_at ? (
                      <>
                        <i className="fas fa-calendar-check text-gray-500 dark:text-gray-400"></i>
                        <span>
                          {formatDate(completedResponse.submitted_at)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>Deadline:</span>
                        <span className="font-medium text-navy-700 dark:text-white">{formatDate(cycle.deadline)}</span>
                      </>
                    )}
                  </div>
                  
                  <Button
                    varient={isCompleted ? "secondary" : "primary"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCompleted) {
                        navigate(`/employee-appraisals/response/${completedResponse.id}`);
                      } else {
                        navigate(`/employee-appraisals/self/${cycle.id}`);
                      }
                    }}
                    disabled={!isCompleted && isDeadlinePassed(cycle.deadline)}
                  >
                    {isCompleted ? "View Response" : "Fill Review"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default SelfReviewTab;
